import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { Popover } from '@dealstack/frontend/shared/components/Popover/Popover';
import React, { BaseSyntheticEvent, memo, SyntheticEvent, useCallback, useState } from 'react';
import styled, { css } from 'styled-components';

interface Props {
	className?: string;
	children?: React.ReactNode;
	expandableRow?: React.ReactNode;
	onClick?: (event: BaseSyntheticEvent) => void;
	isInvalid?: boolean;
	popoverMessage?: string;
}

const Row = ({ className, children, onClick, popoverMessage, expandableRow, ...rest }: Props) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const toggleExpand = useCallback(
		(event: SyntheticEvent) => {
			event.preventDefault();
			setIsExpanded(!isExpanded);
		},
		[isExpanded]
	);

	if (popoverMessage) {
		return (
			<Popover content={<MessageWrapper variant="p-sm">{popoverMessage}</MessageWrapper>}>
				<div className={`${className} ${isExpanded ? 'isExpanded' : ''}`} onClick={onClick}>
					<DefaultRowContent onClick={expandableRow ? toggleExpand : undefined}>
						{children}
					</DefaultRowContent>
					{isExpanded ? expandableRow : null}
				</div>
			</Popover>
		);
	}

	return (
		<div
			className={`${className} ${isExpanded ? 'isExpanded' : ''}`}
			onClick={onClick}
			{...rest}
		>
			<DefaultRowContent onClick={expandableRow ? toggleExpand : undefined}>
				{children}
			</DefaultRowContent>
			{isExpanded ? expandableRow : null}
		</div>
	);
};

const MessageWrapper = styled(Text)`
	white-space: pre-line;
`;

export const DefaultRowContent = styled.div`
	display: contents;
`;
export const ExpandRowContent = styled.div`
	grid-column: 1/-1;
	margin: 1rem;
`;

const Memoized = memo(Row);
const Styled = styled(Memoized)`
	display: flex;
	flex-direction: column;

	${({ expandableRow }) =>
		expandableRow
			? css`
					& > ${DefaultRowContent} {
						cursor: pointer;
					}
				`
			: ''};

	&.isExpanded {
		background-color: ${({ theme }) => theme.v2.color.neutral[300]} !important;
	}

	${({ isInvalid, theme }) =>
		isInvalid
			? css`
					border: 1px dashed ${theme.v2.color.error[600]};
				`
			: ''};
`;

export { Styled as Row };
